import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { useSquawkPlayer } from "./squawk-player/SquawkPlayerContext";

/**
 * Surfaced while the user's preferred (non-v2) transport is flapping — it has
 * retried at least once but hasn't connected and hasn't yet auto-fallen onto
 * another transport (the classic corporate-firewall WebRTC case: connects, then
 * drops after ~15s on ICE consent timeout).
 *
 * The auto-fallback chain (WebRTC → v2 → …) will move the user onto v2 on its
 * own once WebRTC exhausts its retries; this banner just communicates what's
 * happening and offers an early one-click jump so the user doesn't sit through
 * the remaining retries. Clicking "Switch to v2 now" runs `switchToV2` inside
 * the user gesture; once v2 connects it's persisted as their default and the
 * banner clears (isPrimaryRetrying goes false the moment fallbackOverride is set
 * or the transport connects).
 */
export const SquawkNetworkIssueBanner = () => {
  const { isPrimaryRetrying, selectedChannel, switchToV2 } = useSquawkPlayer();

  if (!isPrimaryRetrying || !selectedChannel) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-900/50 px-4 py-3">
      <div className="mx-auto flex items-center justify-center gap-4 sm:gap-6">
        <ExclamationTriangleIcon className="hidden sm:block h-5 w-5 shrink-0 text-amber-500 dark:text-amber-400" />
        <div className="text-center sm:text-left">
          <p className="text-sm font-semibold leading-tight text-amber-700 dark:text-amber-300">
            Network issue detected on your default connection
          </p>
          <p className="mt-0.5 text-xs text-amber-600/80 dark:text-amber-400/80">
            We&apos;re retrying {selectedChannel.name} and will switch to our
            resilient v2 connection automatically — or switch now.
          </p>
        </div>
        <button
          onClick={switchToV2}
          className="shrink-0 inline-flex items-center px-4 py-1.5 bg-amber-600 text-white text-sm font-bold rounded-md hover:bg-amber-700 transition-colors shadow-sm cursor-pointer"
        >
          Switch to v2 now
        </button>
      </div>
    </div>
  );
};
